import React, { useEffect, useState } from 'react';
import { getApiBaseUrl } from '../../utils/environmentConfig';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || getApiBaseUrl();

const subjectToDir = (s) => {
  if (!s) return '';
  const overrides = {
    'Home Economics HL': 'Home_EC_HL',
  };
  return overrides[s] || s.replace(/\s+/g, '_');
};

const TopicBrowser = () => {
  const [subjects, setSubjects] = useState([]);
  const [subject, setSubject] = useState('');
  const [topics, setTopics] = useState([]);
  const [topic, setTopic] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await fetch(`${API_BASE_URL}/api/exam-papers/subjects`);
      const data = await res.json();
      setSubjects(data.subjects || []);
    })();
  }, []);

  useEffect(() => {
    setTopics([]);
    setTopic('');
    setItems([]);
    if (!subject) return;
    (async () => {
      const res = await fetch(`${API_BASE_URL}/api/exam-papers/${subject}/topics`);
      const data = await res.json();
      setTopics(data.topics || []);
    })();
  }, [subject]);

  const loadQuestions = async (nextTopic) => {
    const chosenTopic = nextTopic ?? topic;
    if (!subject || !chosenTopic) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/exam-papers/${subject}/topics/${encodeURIComponent(chosenTopic)}/questions?limit=200`);
      const data = await res.json();
      const all = data.items || [];
      const dir = `/${subjectToDir(subject).toLowerCase()}/`;
      const filtered = all.filter((it) => {
        const url = (it?.parent_image_url || it?.image_url || '').toLowerCase();
        return typeof url === 'string' && url.includes(dir);
      });
      setItems(filtered);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSubject = (s) => {
    setSubject(s);
  };

  const handleSelectTopic = (t) => {
    setTopic(t);
    loadQuestions(t);
  };

  const handleBackFromTopics = () => {
    setSubject('');
    setTopics([]);
    setTopic('');
    setItems([]);
  };

  const handleBackFromQuestions = () => {
    setItems([]);
    setTopic('');
  };

  const buildUrl = (u) => (u && /^https?:\/\//i.test(u)) ? u : `${API_BASE_URL}${u || ''}`;

  return (
    <div className="container mx-auto px-4 py-6">
      <h2 className="text-2xl font-semibold mb-4">Browse by Topic</h2>

      {/* Step 1: Subjects catalogue */}
      {!subject && (
        <div>
          <p className="text-sm text-gray-600 mb-3">Choose a subject to browse its topics.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {subjects.map((s) => (
              <button
                key={s}
                onClick={() => handleSelectSubject(s)}
                className="border rounded p-4 text-left hover:border-blue-500 hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <div className="font-medium">{s}</div>
                <div className="text-xs text-gray-500 mt-1">Explore topics</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Topics catalogue */}
      {subject && !topic && items.length === 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-gray-700">Subject: <span className="font-medium">{subject}</span></div>
            <button className="text-blue-600 underline text-sm" onClick={handleBackFromTopics}>Back to subjects</button>
          </div>
          <p className="text-sm text-gray-600 mb-3">Select a topic.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {topics.map((t) => (
              <button
                key={t}
                onClick={() => handleSelectTopic(t)}
                className="border rounded p-3 text-left hover:border-blue-500 hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <div className="text-sm">{t}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 3: Questions list */}
      {subject && topic && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-gray-700">
              Subject: <span className="font-medium">{subject}</span>
              <span className="mx-2">/</span>
              Topic: <span className="font-medium">{topic}</span>
            </div>
            <div className="flex items-center gap-3">
              <button className="text-blue-600 underline text-sm" onClick={handleBackFromQuestions} disabled={loading}>Back to topics</button>
            </div>
          </div>

          {loading && <div className="text-sm text-gray-600 mb-3">Loading…</div>}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((it, idx) => (
              <div key={idx} className="border rounded p-3">
                <div className="text-sm text-gray-600 mb-1">{subject} • {it.topic}</div>
                <div className="text-xs text-gray-500 mb-2">
                  {it.context?.year ? `Year: ${it.context.year} ` : ''}
                  {it.context?.paper ? `• Paper: ${it.context.paper}` : ''}
                  {it.context?.level ? ` • Level: ${it.context.level}` : ''}
                  {it.context?.section ? ` • ${it.context.section}` : ''}
                  {it.context?.question_label ? ` • ${it.context.question_label}` : ''}
                </div>
                <div className="text-sm mb-2 whitespace-pre-line">{it.question_text}</div>
                {it.image_url && (
                  <img src={buildUrl(it.image_url)} alt="question" className="w-full h-auto border rounded" />
                )}
                {it.parent_image_url && it.parent_image_url !== it.image_url && (
                  <a href={buildUrl(it.parent_image_url)} target="_blank" rel="noreferrer" className="text-blue-600 underline text-sm mt-2 inline-block">Open full page</a>
                )}
              </div>
            ))}
          </div>

          {!loading && items.length === 0 && (
            <div className="text-sm text-gray-600">No questions found for this topic.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default TopicBrowser; 